// A full-body celebration when a sync finishes: the sanglier either kicks back
// with a nice beer, or throws a petanque boule like a boss. Variant is picked at
// random by the caller. Decorative only - respects prefers-reduced-motion (CSS).
import { cn } from '../lib/utils';

export const BOAR_MOODS = ['cheers', 'petanque'];

// The recognizable boar head (from BoarMark, no background), snout facing right.
function Head() {
  return (
    <g transform="translate(8,-2) scale(0.72)">
      <path d="M22 23 C18 12 23 7 28 11 C30 14 30 19 29 24 Z" fill="#8A5A38" />
      <path d="M24.5 22 C22.5 15 25 12 27.5 14 C29 17 28.5 20 27.5 23 Z" fill="#DE949B" />
      <path d="M42 21 C45 8 52 7 53 15 C53.5 20 50 24 46 24 Z" fill="#8A5A38" />
      <path d="M44 21 C46.5 12 50.5 12 51 16.5 C51 20.5 49 23 46 23 Z" fill="#DE949B" />
      <path d="M22 22 C31 16 41 17 46 24 C48 27 49 30 50 33 C54 34 59 36 59 40 C59 44 56 47 51 47 C49 50 46 51 43 51 C41 55 38 57 34 56 C27 56 22 52 20 46 C17 41 17 33 18 28 C19 24 20 22 22 22 Z" fill="#9E6A43" />
      <path d="M19 26 L21 13 L25.5 21 L30 12 L34 21 L38 13 L41 23 C42 29 40 37 35 41 C29 42 23 40 19 35 C17 31 17.5 28 19 26 Z" fill="#5C3B26" />
      <ellipse cx="53.2" cy="41" rx="6.9" ry="5.5" fill="#BC8067" />
      <ellipse cx="50.6" cy="41" rx="1.5" ry="2.4" fill="#4A2C20" />
      <ellipse cx="55.4" cy="41" rx="1.5" ry="2.4" fill="#4A2C20" />
      <path d="M47 50.5 C43.3 48 42.3 41.8 45.5 38 C47.6 41 47 46.7 50.7 49.3 Z" fill="#F5EEDD" stroke="#CBB086" strokeWidth="0.7" />
      <circle cx="31" cy="30" r="2.8" fill="#fff" />
      <circle cx="41" cy="29.5" r="3.3" fill="#fff" />
      <circle cx="31.6" cy="30.2" r="1.5" fill="#241A12" />
      <circle cx="41.8" cy="29.8" r="1.8" fill="#241A12" />
      <path d="M27.6 25 Q31 22.8 34.2 25" stroke="#4A2E1C" strokeWidth="2.2" fill="none" strokeLinecap="round" />
      <path d="M37 24 Q41 21.6 45 24.2" stroke="#4A2E1C" strokeWidth="2.6" fill="none" strokeLinecap="round" />
    </g>
  );
}

function Cheers({ size }) {
  return (
    <svg height={size} width={size * 0.8} viewBox="0 0 80 100" role="img" aria-label="Boar having a beer">
      <ellipse cx="38" cy="95" rx="22" ry="3.5" fill="rgba(0,0,0,.12)" />
      <g className="boar-idle" style={{ transformBox: 'fill-box', transformOrigin: '50% 100%' }}>
        <path d="M17 60 q-7 -2 -6 5 q1 4 5 2" fill="#5C3B26" />
        <rect x="28" y="74" width="10" height="18" rx="5" fill="#6B4227" />
        <rect x="40" y="74" width="11" height="18" rx="5" fill="#7A5133" />
        <rect x="27" y="88" width="12" height="5" rx="2.5" fill="#4A2C20" />
        <rect x="39" y="88" width="13" height="5" rx="2.5" fill="#4A2C20" />
        <ellipse cx="38" cy="60" rx="19" ry="19" fill="#9E6A43" />
        <ellipse cx="38" cy="63" rx="12" ry="13" fill="#B98A63" />
        <path d="M22 52 q-4 6 2 12 q4 2 7 -1" fill="none" stroke="#8A5C39" strokeWidth="6" strokeLinecap="round" />
        <Head />
        <path d="M50 52 q10 -2 8 -14" fill="none" stroke="#8A5C39" strokeWidth="6" strokeLinecap="round" />
        {/* raised mug (sips) */}
        <g transform="translate(50,20)">
          <g className="boar-sip" style={{ transformBox: 'fill-box', transformOrigin: '50% 100%' }}>
            <path d="M0 4 a3 3 0 0 1 3-3 a3.2 3.2 0 0 1 6 0 a3 3 0 0 1 3 3 Z" fill="#FFF7E0" stroke="#E6D4A0" strokeWidth="0.5" />
            <rect x="0.3" y="3.6" width="11" height="13" rx="1.6" fill="#F3B23A" stroke="#B98324" strokeWidth="0.9" />
            <rect x="1.1" y="6.4" width="9.4" height="9.6" rx="1" fill="#E39A1B" />
            <path d="M11.3 5.4 a3.8 3.8 0 0 1 0 8" fill="none" stroke="#B98324" strokeWidth="1.7" />
          </g>
        </g>
      </g>
    </svg>
  );
}

function Petanque({ size }) {
  return (
    <svg height={size} width={size * 0.96} viewBox="0 0 96 100" role="img" aria-label="Boar playing petanque">
      <defs>
        <radialGradient id="cb-boule" cx="36%" cy="30%" r="72%">
          <stop offset="0" stopColor="#F4F6F9" />
          <stop offset="45%" stopColor="#AEB4BE" />
          <stop offset="100%" stopColor="#565D68" />
        </radialGradient>
      </defs>
      <ellipse cx="42" cy="95" rx="26" ry="3.5" fill="rgba(0,0,0,.12)" />
      <g className="boar-idle" style={{ transformBox: 'fill-box', transformOrigin: '50% 100%' }}>
        <path d="M17 60 q-7 -2 -6 5 q1 4 5 2" fill="#5C3B26" />
        <rect x="26" y="74" width="11" height="18" rx="5" fill="#6B4227" />
        <rect x="44" y="72" width="11" height="20" rx="5" fill="#7A5133" transform="rotate(10 49 82)" />
        <rect x="25" y="88" width="13" height="5" rx="2.5" fill="#4A2C20" />
        <rect x="46" y="88" width="14" height="5" rx="2.5" fill="#4A2C20" />
        <ellipse cx="38" cy="60" rx="19" ry="19" fill="#9E6A43" />
        <ellipse cx="38" cy="63" rx="12" ry="13" fill="#B98A63" />
        {/* back hand raises a beer - boss move */}
        <path d="M24 52 q-6 3 -6 9" fill="none" stroke="#8A5C39" strokeWidth="6" strokeLinecap="round" />
        <g transform="translate(11,49) rotate(-10)">
          <path d="M0 3 a2.4 2.4 0 0 1 2.4-2.4 a2.7 2.7 0 0 1 5 0 a2.4 2.4 0 0 1 2.4 2.4 Z" fill="#FFF7E0" stroke="#E6D4A0" strokeWidth="0.5" />
          <rect x="0.3" y="2.8" width="9.2" height="11" rx="1.3" fill="#F3B23A" stroke="#B98324" strokeWidth="0.8" />
          <rect x="1" y="5.2" width="7.8" height="8" rx="0.8" fill="#E39A1B" />
          <path d="M9.3 4.4 a3.2 3.2 0 0 1 0 6.6" fill="none" stroke="#B98324" strokeWidth="1.5" />
        </g>
        <Head />
        {/* shades = boss mode */}
        <g fill="#15181d">
          <rect x="26.5" y="18.5" width="8.5" height="6.5" rx="2.2" />
          <rect x="35.5" y="18" width="9.5" height="7" rx="2.4" />
          <path d="M45 20 q3 -1 5 1" stroke="#15181d" strokeWidth="1.6" fill="none" />
        </g>
        {/* throwing arm, following through */}
        <path d="M50 51 q14 1 22 8" fill="none" stroke="#8A5C39" strokeWidth="6" strokeLinecap="round" />
      </g>
      {/* the thrown boule rolls forward */}
      <path d="M88 86 q4 -1 7 0" stroke="#bbb" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity=".55" />
      <g className="boar-roll-away" style={{ transformBox: 'fill-box', transformOrigin: '50% 50%' }}>
        <circle cx="82" cy="86" r="7" fill="url(#cb-boule)" />
        <ellipse cx="79" cy="83" rx="1.8" ry="1.2" fill="#fff" opacity=".7" />
      </g>
    </svg>
  );
}

export default function CelebratingBoar({ mood = 'cheers', size = 48, className = '' }) {
  return (
    <span className={cn('inline-block', className)} style={{ lineHeight: 0 }}>
      {mood === 'petanque' ? <Petanque size={size} /> : <Cheers size={size} />}
    </span>
  );
}
